import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, MessageCircle, Calendar, Star, Shield, Clock, Award, MapPin, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Layout from '@/components/layout/Layout';
import ServiceCard from '@/components/ServiceCard';
import ReviewCard from '@/components/ReviewCard';
import BookingModal from '@/components/BookingModal';
import HeroSection from '@/components/HeroSection';
import MediaGallery from '@/components/MediaGallery';
import MediaCarousel from '@/components/MediaCarousel';
import FAQSection from '@/components/FAQSection';
import SEOHead from '@/components/seo/SEOHead';
// LocalBusiness schema is in index.html - no duplicate injection needed
import iphoneImage from '@/assets/iphone-repair.jpg';
import macbookImage from '@/assets/macbook-repair.jpg';
import ipadImage from '@/assets/ipad-repair.jpg';
import watchImage from '@/assets/watch-repair.jpg';

// Custom hook for scroll-triggered animations
const useScrollReveal = (threshold = 0.1) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold, rootMargin: '0px 0px -80px 0px' }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [threshold]);

  return { ref, isVisible };
};

const Index = () => {
  const [isBookingOpen, setIsBookingOpen] = useState(false);

  const services = [
    {
      title: 'iPhone Service',
      description: 'Expert iPhone repair and iPhone care services including screen replacement and battery replacement. iPhone service center for all models.',
      image: iphoneImage,
      link: '/iphone-service',
      altText: 'iPhone screen replacement and battery repair at Apple service center Patna Bihar - iPhone repair shop near me',
    },
    {
      title: 'MacBook Service',
      description: 'Professional MacBook service center for screen, battery, keyboard, and logic board repairs. Mac service center near me in Patna.',
      image: macbookImage,
      link: '/macbook-service',
      altText: 'MacBook service center near me in Patna Bihar - MacBook Pro and Air screen keyboard battery repair',
    },
    {
      title: 'iPad Service',
      description: 'Complete iPad repair services from screen damage to charging issues. Best iPad repair near me for all models.',
      image: ipadImage,
      link: '/ipad-service',
      altText: 'iPad repair services in Patna Bihar - screen replacement charging port fix at Apple service centre',
    },
    {
      title: 'Apple Watch Repair',
      description: 'Precision Apple Watch repair for screen, battery, and sensors. Apple apple watch repair service from Series 3 to Ultra.',
      image: watchImage,
      link: '/watch-service',
      altText: 'Apple Watch repair in Patna Bihar - screen battery sensor replacement at Apple care center near me',
    },
  ];

  const reviews = [
    {
      name: 'Rahul Kumar',
      rating: 5,
      review: 'Best iPhone service center in Patna! Got my iPhone screen replacement done in just 2 hours. The quality is exactly like the original. Highly recommended Apple repair shop near me.',
      date: '2 weeks ago',
    },
    {
      name: 'Priya Singh',
      rating: 5,
      review: 'Best Apple service center in Bihar. They fixed my MacBook Pro battery issue at their MacBook service center near me. The staff is very knowledgeable and helpful.',
      date: '1 month ago',
    },
    {
      name: 'Amit Verma',
      rating: 5,
      review: 'Best iPad repair services in Patna. My iPad was not charging and they fixed it within a day. Fair pricing and genuine parts at this Apple service centre.',
      date: '3 weeks ago',
    },
    {
      name: 'Sneha Sharma',
      rating: 5,
      review: 'Apple Watch repair done perfectly at this Apple care center near me. Quick service with warranty. Great experience at this Apple repair centre near me!',
      date: '1 week ago',
    },
  ];

  const features = [
    {
      icon: Shield,
      title: 'Genuine Parts',
      description: 'We use only authentic Apple parts for all repairs',
    },
    {
      icon: Clock,
      title: 'Same Day Repair',
      description: 'Most repairs completed within 2-4 hours',
    },
    {
      icon: Award,
      title: 'Expert Technicians',
      description: 'Apple certified repair specialists',
    },
  ];

  // Scroll reveal refs
  const featuresReveal = useScrollReveal(0.2);
  const servicesReveal = useScrollReveal(0.15);
  const reviewsReveal = useScrollReveal(0.15);
  const contactReveal = useScrollReveal(0.15);
  const ctaReveal = useScrollReveal(0.2);

  return (
    <Layout>
      <SEOHead
        title="Apple Service Center Patna Bihar | iPhone MacBook Repair"
        description="Best Apple service center in Patna Bihar. Expert iPhone, MacBook, iPad repair with genuine parts. Same day service. Call +91 97986 53591"
        url="https://ifixappleservicecenter.in"
      />
      {/* LocalBusiness + WebSite schemas are in index.html head - no React injection to avoid duplicates */}
      {/* Hero Section */}
      <HeroSection onBookingOpen={() => setIsBookingOpen(true)} />

      {/* Features Section with parallax */}
      <section 
        ref={featuresReveal.ref}
        className={`section-padding bg-secondary transition-all duration-1000 ${
          featuresReveal.isVisible ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <div className="container-apple">
          <div className={`grid grid-cols-1 md:grid-cols-3 gap-8 stagger-children`}>
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className={`flex items-start gap-4 transition-all duration-700 ${
                  featuresReveal.isVisible 
                    ? 'opacity-100 translate-y-0' 
                    : 'opacity-0 translate-y-12'
                }`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section with parallax */}
      <section 
        ref={servicesReveal.ref}
        className="section-padding" 
        id="services"
      >
        <div className="container-apple">
          <div 
            className={`text-center mb-16 transition-all duration-700 ${
              servicesReveal.isVisible 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-12'
            }`}
          >
            <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-4">
              Our Services
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Comprehensive repair services for all Apple devices. From{' '}
              <Link to="/iphone-service" className="text-primary hover:underline">iPhone screen replacements</Link> to complex{' '}
              <Link to="/macbook-service" className="text-primary hover:underline">MacBook logic board repairs</Link>, we handle it all.
              Check our <Link to="/blog" className="text-primary hover:underline">repair guides</Link> for tips and information.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <div
                key={service.title}
                className={`transition-all duration-700 ${
                  servicesReveal.isVisible 
                    ? 'opacity-100 translate-y-0 scale-100' 
                    : 'opacity-0 translate-y-16 scale-95'
                }`}
                style={{ transitionDelay: `${200 + index * 100}ms` }}
              >
                <ServiceCard
                  {...service}
                  delay={0}
                />
              </div>
            ))}
          </div>
          
          {/* Additional service links - Enhanced internal linking */}
          <div className="mt-12 text-center">
            <p className="text-muted-foreground mb-4">
              Looking for specific repairs? Check out our detailed service pages:
            </p>
            <div className="flex flex-wrap justify-center gap-3 mb-4">
              <Link to="/iphone-screen-replacement" className="text-primary hover:underline text-sm font-medium">
                iPhone Screen Replacement
              </Link>
              <span className="text-border">•</span>
              <Link to="/iphone-battery-replacement" className="text-primary hover:underline text-sm font-medium">
                iPhone Battery Replacement
              </Link>
              <span className="text-border">•</span>
              <Link to="/service-areas" className="text-primary hover:underline text-sm font-medium">
                Service Areas in Bihar
              </Link>
            </div>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/blog/iphone-battery-draining-fast-fix-patna" className="text-primary hover:underline text-sm">
                Battery Draining Fast
              </Link>
              <span className="text-border">•</span>
              <Link to="/blog/iphone-not-charging-solutions-bihar" className="text-primary hover:underline text-sm">
                iPhone Not Charging
              </Link>
              <span className="text-border">•</span>
              <Link to="/blog/iphone-overheating-causes-fix-patna" className="text-primary hover:underline text-sm">
                iPhone Overheating
              </Link>
              <span className="text-border">•</span>
              <Link to="/blog/iphone-water-damage-recovery-patna" className="text-primary hover:underline text-sm">
                Water Damage Recovery
              </Link>
              <span className="text-border">•</span>
              <Link to="/blog/macbook-battery-replacement-patna-guide" className="text-primary hover:underline text-sm">
                MacBook Battery Guide
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Section with parallax */}
      <section 
        ref={reviewsReveal.ref}
        className="section-padding bg-secondary"
      >
        <div className="container-apple">
          <div 
            className={`flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12 transition-all duration-700 ${
              reviewsReveal.isVisible 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-12'
            }`}
          >
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="flex gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-[hsl(45_93%_47%)] text-[hsl(45_93%_47%)]" />
                  ))}
                </div>
                <span className="text-lg font-semibold">4.9 on Google</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-semibold text-foreground">
                What Our Customers Say
              </h2>
            </div>
            <a
              href="https://g.co/kgs/ifixapplecare"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-primary font-medium hover:gap-3 transition-all"
            >
              View all reviews on Google
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {reviews.map((review, index) => (
              <div
                key={review.name}
                className={`transition-all duration-700 ${
                  reviewsReveal.isVisible 
                    ? 'opacity-100 translate-y-0 scale-100' 
                    : 'opacity-0 translate-y-16 scale-95'
                }`}
                style={{ transitionDelay: `${200 + index * 100}ms` }}
              >
                <ReviewCard {...review} delay={0} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Media Gallery Section */}
      <MediaGallery />

      {/* Media Carousel Section */}
      <MediaCarousel />

      {/* FAQ Section */}
      <FAQSection />

      {/* Map & Contact Section with parallax */}
      <section 
        ref={contactReveal.ref}
        className="section-padding" 
        id="contact"
      >
        <div className="container-apple">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div 
              className={`transition-all duration-700 ${
                contactReveal.isVisible 
                  ? 'opacity-100 translate-x-0' 
                  : 'opacity-0 -translate-x-16'
              }`}
            >
              <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-6">
                Visit Our Store
              </h2>
              <p className="text-muted-foreground mb-8">
                Located in the heart of Patna, our service center is easily accessible. Drop by for a free diagnosis of your{' '}
                <Link to="/iphone-service" className="text-primary hover:underline">iPhone</Link>,{' '}
                <Link to="/macbook-service" className="text-primary hover:underline">MacBook</Link>,{' '}
                <Link to="/ipad-service" className="text-primary hover:underline">iPad</Link>, or{' '}
                <Link to="/watch-service" className="text-primary hover:underline">Apple Watch</Link>.
                Book an appointment for priority service.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-secondary flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">Address</h4>
                    <p className="text-muted-foreground">
                      Shop No. 6, Jassinandan Plaza,<br />
                      New Dak Bunglow Rd, Patna, Bihar 800001
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-secondary flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">Phone</h4>
                    <a href="tel:9798653591" className="text-primary hover:underline">
                      +91 97986 53591
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-secondary flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">Hours</h4>
                    <p className="text-muted-foreground">
                      Monday - Saturday: 10:00 AM - 8:00 PM<br />
                      Sunday: 11:00 AM - 5:00 PM
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-4 mt-8">
                <Button variant="apple-blue" onClick={() => setIsBookingOpen(true)}>
                  <Calendar className="w-5 h-5" />
                  Book Appointment
                </Button>
                <Button variant="apple-outline" asChild>
                  <a
                    href="https://maps.google.com/?q=iFix+Apple+Care+Patna"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Get Directions
                    <ChevronRight className="w-4 h-4" />
                  </a>
                </Button>
              </div>
            </div>
            
            <div 
              className={`h-[400px] lg:h-full min-h-[400px] rounded-3xl overflow-hidden shadow-apple-lg transition-all duration-700 ${
                contactReveal.isVisible 
                  ? 'opacity-100 translate-x-0 scale-100' 
                  : 'opacity-0 translate-x-16 scale-95'
              }`}
              style={{ transitionDelay: '200ms' }}
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3597.9762654456!2d85.1357!3d25.6094!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2siFix+Apple+Care!5e0!3m2!1sen!2sin!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="iFix Apple Care Location"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section with parallax */}
      <section 
        ref={ctaReveal.ref}
        className="section-padding bg-foreground overflow-hidden"
      >
        <div 
          className={`container-apple text-center transition-all duration-700 ${
            ctaReveal.isVisible 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-12'
          }`}
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-background mb-4">
            Ready to Fix Your Device?
          </h2>
          <p className="text-background/70 mb-6 max-w-xl mx-auto">
            Get expert repair service for your Apple devices. Book an appointment now or chat with us on WhatsApp for immediate assistance.
          </p>
          
          {/* Quick links - Enhanced internal linking */}
          <div className="flex flex-wrap justify-center gap-3 mb-6 text-sm">
            <Link to="/iphone-service" className="text-background/80 hover:text-background underline">
              iPhone Repair
            </Link>
            <Link to="/iphone-screen-replacement" className="text-background/80 hover:text-background underline">
              Screen Replacement
            </Link>
            <Link to="/iphone-battery-replacement" className="text-background/80 hover:text-background underline">
              Battery Replacement
            </Link>
            <Link to="/macbook-service" className="text-background/80 hover:text-background underline">
              MacBook Repair
            </Link>
            <Link to="/ipad-service" className="text-background/80 hover:text-background underline">
              iPad Repair
            </Link>
            <Link to="/watch-service" className="text-background/80 hover:text-background underline">
              Watch Repair
            </Link>
          </div>
          <div className="flex flex-wrap justify-center gap-3 mb-8 text-sm">
            <Link to="/blog" className="text-background/80 hover:text-background underline">
              Repair Guides
            </Link>
            <Link to="/service-areas" className="text-background/80 hover:text-background underline">
              Service Areas
            </Link>
            <Link to="/franchise" className="text-background/80 hover:text-background underline">
              Franchise
            </Link>
            <Link to="/updates" className="text-background/80 hover:text-background underline">
              Latest Updates
            </Link>
          </div>
          
          <div 
            className={`flex flex-col sm:flex-row gap-4 justify-center transition-all duration-700 ${
              ctaReveal.isVisible 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-8'
            }`}
            style={{ transitionDelay: '200ms' }}
          >
            <Button 
              variant="apple-blue" 
              size="lg"
              onClick={() => setIsBookingOpen(true)}
            >
              <Calendar className="w-5 h-5" />
              Book Appointment
            </Button>
            <Button variant="call" size="lg" asChild>
              <a href="tel:9798653591">
                <Phone className="w-5 h-5" />
                Call +91 97986 53591
              </a>
            </Button>
          </div>
        </div>
      </section>

      <BookingModal isOpen={isBookingOpen} onClose={() => setIsBookingOpen(false)} />
    </Layout>
  );
};

export default Index;
